import { ReactElement, useMemo } from 'react';
import styles from './task-info-item.module.scss';
import { TaskInfoItem } from './task-info-item';
import { useTaskBeaconSnapshot, useTaskLocations } from '../providers';
import { useGeocode, useW3w } from '../queries';
import { BeaconSnapshot, TaskLocation } from '@/api';
import { UITagProps } from '@/ui';
import { useTasksTranslation } from '../hooks';
import { formatGpsCoordinates } from '../util/task-helpers';

type TaskLastLocationListProps = {
    onViewAllBeacons?: () => void;
};
export function TaskLastLocationList(
    props: TaskLastLocationListProps
): ReactElement {
    const { onViewAllBeacons } = props;

    const { latestLocation } = useTaskLocations();
    const { beacons, updatedAtStr } = useTaskBeaconSnapshot();

    return (
        <div className={styles.container}>
            <div className={styles.listContainer}>
                {latestLocation && (
                    <>
                        <AddressItem location={latestLocation} />
                        <W3wItem location={latestLocation} />
                    </>
                )}

                <CoordinatesItem location={latestLocation} />

                <BeaconsItem
                    beacons={beacons}
                    beaconsUpdatedAt={updatedAtStr}
                    onClick={onViewAllBeacons}
                />
            </div>
        </div>
    );
}

function CoordinatesItem(props: {
    location: TaskLocation | null;
}): ReactElement {
    const { location } = props;
    const { t } = useTasksTranslation();

    const title = t('shared.tasks.gps_coordinates');

    const [text, copyText] = useMemo(() => {
        if (location) {
            const lat = location.lat;
            const lng = location.lng;
            const acc = location.acc;

            const text = formatGpsCoordinates(t, lat, lng, acc);
            const copyText = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

            return [text, copyText];
        } else {
            return [t('shared.tasks.no_gps_data'), null];
        }
    }, [location, t]);

    return (
        <TaskInfoItem
            title={title}
            text={text}
            copyToClipboardText={copyText}
        />
    );
}

function AddressItem(props: { location: TaskLocation }): ReactElement {
    const { location } = props;

    const { t } = useTasksTranslation();
    const { status, data } = useGeocode(location);

    const title = t('shared.tasks.address');

    const [text, copyText] = useMemo(() => {
        if (status === 'loading') {
            return [t('shared.labels.loading'), null];
        } else if (status === 'error') {
            return [t('shared.labels.error'), null];
        } else if (status === 'success' && data) {
            return [data, data];
        } else {
            return [t('shared.labels.no_data'), null];
        }
    }, [status, data, t]);

    return (
        <TaskInfoItem
            title={title}
            text={text}
            copyToClipboardText={copyText}
        />
    );
}

function W3wItem(props: { location: TaskLocation }): ReactElement {
    const { location } = props;

    const { t } = useTasksTranslation();
    const { status, data } = useW3w(location);

    // A brand name, so it stays as it is written everywhere else.
    const title = 'What3Words';

    const [text, copyText] = useMemo(() => {
        if (status === 'loading') {
            return [t('shared.labels.loading'), null];
        } else if (status === 'error') {
            return [t('shared.labels.error'), null];
        } else if (status === 'success' && data) {
            return [data, data];
        } else {
            return [t('shared.labels.no_data'), null];
        }
    }, [status, data, t]);

    return (
        <TaskInfoItem title={title} w3w={text} copyToClipboardText={copyText} />
    );
}

function BeaconsItem(props: {
    beacons: BeaconSnapshot[];
    beaconsUpdatedAt: string | null;
    onClick?: () => void;
}): ReactElement {
    const { beacons, beaconsUpdatedAt, onClick } = props;
    const { t } = useTasksTranslation();

    const title = t('components.task_last_location_list.nearest_beacon');

    const [text, tags] = useMemo<[string, UITagProps[]]>(() => {
        if (beacons && beacons.length > 0) {
            const beacon = beacons[0];
            return [
                t('shared.tasks.beacon_name_with_distance', {
                    name: beacon.name,
                    distance: beacon.distanceMeters
                }),
                beacon.tags.map((tag) => ({ text: tag }))
            ];
        } else {
            return [
                t('components.task_last_location_list.no_nearby_beacons'),
                []
            ];
        }
    }, [beacons, t]);

    return (
        <TaskInfoItem
            title={title}
            text={text}
            description={beaconsUpdatedAt}
            tags={tags}
            onClick={onClick}
            onHoverText={t(
                'components.task_last_location_list.view_all_beacons'
            )}
        />
    );
}
